/**
 * 知识库 —— 需求文档问答（P0 文档/问答 + P1 规则治理/评估看板）
 * 布局按编码架构规范 5.9：aurora-page--fit 三段式骨架（page-header / search-bar / content-card）
 * Tab1 文档管理：上传（冲突三选一）/ 列表 / 分块预览 / 原文预览 / 重新入库 / 作废 / 删除
 * Tab2 智能问答：见 Chat.tsx（P1 增加冲突拒答卡与反馈）
 * Tab3 规则管理：见 Rules.tsx（L2 规则级治理）
 * Tab4 评估看板：见 EvalDashboard.tsx（Golden Set → 指标 → 上线门禁）
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useProjectStore } from '@/stores/project'
import {
  Button, Drawer, Form, Input, Modal, Popconfirm, Popover, Radio, Select,
  Space, Table, Tabs, Tag, Tooltip, Typography, Upload, message,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import type { UploadFile } from 'antd'
import { FileTextOutlined, UploadOutlined } from '@ant-design/icons'
import '../../styles/aurora-page.css'
import '../../styles/aurora-modal.css'
import {
  type KBChunk, type KBDocument, archiveDocument, checkConflict, deleteDocument, getDocChunks,
  getDocContent, getDocuments, reindexDocument, uploadDocument,
} from '@/api/knowledgeBase'
import { ChatPanel } from './Chat'
import { EvalPanel } from './EvalDashboard'
import { RulesPanel } from './Rules'

const { Title, Text } = Typography

const DOC_TYPE_LABEL: Record<string, string> = { REQUIREMENT: '需求文档', DESIGN: '设计文档', CHANGE_NOTE: '变更单' }

/** 状态列：Tag 只用 antd 预设语义色（规范 5.9.2）；P1 起状态位移：5=规则抽取中/6=就绪/7=失败/8=已作废 */
const STATUS_META: Record<number, { color: string; label: string }> = {
  1: { color: 'warning', label: '待处理' },
  2: { color: 'processing', label: '解析中' },
  3: { color: 'processing', label: '分块中' },
  4: { color: 'processing', label: '向量化中' },
  5: { color: 'processing', label: '规则抽取中' },
  6: { color: 'success', label: '就绪' },
  7: { color: 'error', label: '失败' },
  8: { color: 'default', label: '已作废' },
}

function StatusTag({ record }: { record: KBDocument }) {
  const meta = STATUS_META[record.status] || STATUS_META[1]
  if (record.status === 7) {
    return <Tooltip title={record.fail_reason}><Tag color="error">{meta.label}</Tag></Tooltip>
  }
  if (record.status >= 2 && record.status <= 5) {
    return <Tag color="processing">{meta.label} {record.progress}%</Tag>
  }
  return <Tag color={meta.color}>{meta.label}</Tag>
}

/** 上传弹窗（含冲突三选一） */
function UploadModal({ open, projectId, onClose, onUploaded }: {
  open: boolean; projectId: number; onClose: () => void; onUploaded: () => void
}) {
  const [form] = Form.useForm()
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const [existing, setExisting] = useState<{ id: number; doc_name: string; doc_version: string; is_current: boolean; status: number }[]>([])
  const [relation, setRelation] = useState<number>(1)
  const [replaceIds, setReplaceIds] = useState<number[]>([])
  const [uploading, setUploading] = useState(false)

  const reset = () => {
    setFileList([]); setExisting([]); setRelation(1); setReplaceIds([])
    form.resetFields()
  }

  /** 选好文件与版本后做冲突检测（V-05：归并建议） */
  const handleCheck = async () => {
    const name = fileList[0]?.name
    if (!name) return
    const docName = name.replace(/\.(docx|md|txt)$/i, '')
    try {
      const res = await checkConflict(projectId, docName, form.getFieldValue('doc_version') || 'V1.0')
      setExisting(res.existing || [])
      setRelation(res.existing?.length ? 2 : 1)
    } catch { /* 检测失败不阻塞上传 */ }
  }

  const handleUpload = async () => {
    const file = fileList[0]?.originFileObj
    if (!file) { message.warning('请选择文件'); return }
    if (relation === 3 && replaceIds.length === 0) { message.warning('替代旧版需选择目标文档'); return }
    setUploading(true)
    try {
      await uploadDocument({
        file, project_id: projectId,
        doc_version: form.getFieldValue('doc_version') || 'V1.0',
        doc_type: form.getFieldValue('doc_type') || 'REQUIREMENT',
        relation, replace_doc_ids: relation === 3 ? replaceIds : [],
      })
      message.success('上传成功，正在后台入库')
      reset()
      onUploaded()
      onClose()
    } finally {
      setUploading(false)
    }
  }

  return (
    <Modal title="上传文档" open={open} onCancel={() => { reset(); onClose() }} onOk={handleUpload}
      okText="上传" cancelText="取消" confirmLoading={uploading} width={560} destroyOnClose
      wrapClassName="aurora-modal-wrap" className="aurora-modal"
      footer={(
        <Space>
          <Button onClick={() => { reset(); onClose() }}>取消</Button>
          <Button type="primary" loading={uploading} onClick={handleUpload}>上传</Button>
        </Space>
      )}>
      <Form form={form} layout="vertical" initialValues={{ doc_version: 'V1.0', doc_type: 'REQUIREMENT' }} style={{ marginTop: 4 }}>
        <Form.Item label="文档文件" required>
          <Upload maxCount={1} accept=".docx,.md,.txt" fileList={fileList}
            beforeUpload={() => false} // 手动上传
            onChange={({ fileList: fl }) => setFileList(fl)}>
            <Button icon={<UploadOutlined />}>选择文件（.docx / .md / .txt）</Button>
          </Upload>
        </Form.Item>
        <Space size="middle" style={{ display: 'flex' }}>
          <Form.Item label="业务版本号" name="doc_version" rules={[{ required: true }]}>
            <Input placeholder="如 V2.1" style={{ width: 160 }} onBlur={handleCheck} />
          </Form.Item>
          <Form.Item label="文档类型" name="doc_type">
            <Select style={{ width: 160 }} options={[
              { value: 'REQUIREMENT', label: '需求文档' },
              { value: 'DESIGN', label: '设计文档' },
              { value: 'CHANGE_NOTE', label: '变更单' },
            ]} />
          </Form.Item>
        </Space>

        {existing.length > 0 && (
          <Form.Item label="检测到同名文档，请选择入库关系">
            <Radio.Group value={relation} onChange={(e) => setRelation(e.target.value)}>
              <Space direction="vertical">
                <Radio value={2}>作为新版本（旧版本自动失效，不再参与检索）</Radio>
                <Radio value={3}>替代既有文档（变更单/补充说明，可跨文档指定）</Radio>
                <Radio value={1}>作为全新文档（互不影响）</Radio>
              </Space>
            </Radio.Group>
            {relation === 3 && (
              <Select mode="multiple" placeholder="选择被替代的文档" style={{ width: '100%', marginTop: 8 }}
                value={replaceIds} onChange={setReplaceIds}
                options={existing.map((d) => ({ value: d.id, label: `${d.doc_name} ${d.doc_version}${d.is_current ? '（当前生效）' : ''}` }))} />
            )}
          </Form.Item>
        )}
      </Form>
    </Modal>
  )
}

/** 分块预览抽屉 */
function ChunkDrawer({ doc, onClose }: { doc: KBDocument | null; onClose: () => void }) {
  const [chunks, setChunks] = useState<KBChunk[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!doc) return
    setLoading(true)
    getDocChunks(doc.id).then(setChunks).finally(() => setLoading(false))
  }, [doc])

  return (
    <Drawer title={`分块预览 - ${doc?.doc_name ?? ''} ${doc?.doc_version ?? ''}`} width={720}
      rootClassName="aurora-edit-drawer" open={!!doc} onClose={onClose}>
      <Table
        rowKey="id" loading={loading} dataSource={chunks} size="small"
        pagination={{ pageSize: 20, showTotal: (t) => `共 ${t} 块` }}
        columns={[
          { title: '#', width: 48, render: (_v, _r, i) => i + 1 },
          {
            title: '章节', dataIndex: 'section_path', width: 200, ellipsis: true,
            render: (v, r) => (
              <Space size={4}>
                {r.is_noise ? <Tag color="default">噪声:{r.noise_type || '-'}</Tag> : null}
                {!r.is_parent && r.has_embedding ? <Tag color="processing">已向量化</Tag> : null}
                <span>{v || '-'}</span>
              </Space>
            ),
          },
          {
            title: '内容', dataIndex: 'content',
            render: (v: string) => (
              <Popover content={<div style={{ maxWidth: 560, whiteSpace: 'pre-wrap', maxHeight: 400, overflow: 'auto' }}>{v}</div>} trigger="click">
                <span style={{ cursor: 'pointer' }}>{v.slice(0, 60)}{v.length > 60 ? '…' : ''}</span>
              </Popover>
            ),
          },
          { title: '字数', dataIndex: 'token_count', width: 64 },
        ]}
      />
    </Drawer>
  )
}

/** 原文预览抽屉 */
function ContentDrawer({ doc, onClose }: { doc: KBDocument | null; onClose: () => void }) {
  const [content, setContent] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!doc) return
    setLoading(true)
    getDocContent(doc.id).then((res) => setContent(res.content)).finally(() => setLoading(false))
  }, [doc])

  return (
    <Drawer title={`原文预览 - ${doc?.doc_name ?? ''}`} width={720} rootClassName="aurora-edit-drawer"
      open={!!doc} onClose={onClose}>
      <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 13, color: 'var(--au-slate)' }}>
        {loading ? '加载中…' : content}
      </pre>
    </Drawer>
  )
}

export default function KnowledgeBase() {
  const { currentProject } = useProjectStore()
  const [tab, setTab] = useState('docs')
  const [docs, setDocs] = useState<KBDocument[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [inputKeyword, setInputKeyword] = useState('') // 输入值（实时）
  const [keyword, setKeyword] = useState('') // 生效关键词（点查询/回车才更新）
  const [loading, setLoading] = useState(false)
  const [uploadOpen, setUploadOpen] = useState(false)
  const [chunkDoc, setChunkDoc] = useState<KBDocument | null>(null)
  const [contentDoc, setContentDoc] = useState<KBDocument | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval>>()

  const fetchDocs = useCallback(async (p: number, ps: number, kw: string) => {
    if (!currentProject) return
    setLoading(true)
    try {
      const res = await getDocuments({ project_id: currentProject.id, page: p, page_size: ps, keyword: kw })
      setDocs(res.data); setTotal(res.total)
    } finally { setLoading(false) }
  }, [currentProject])

  // 页码/页大小/生效关键词/项目变化时统一触发请求
  useEffect(() => { fetchDocs(page, pageSize, keyword) }, [fetchDocs, page, pageSize, keyword])

  // 轮询始终调用最新一次的 fetchDocs（避免闭包过期参数）；处理中状态 2-5（解析/分块/向量化/规则抽取）
  const fetchRef = useRef(fetchDocs)
  fetchRef.current = fetchDocs
  useEffect(() => {
    const processing = docs.some((d) => d.status >= 2 && d.status <= 5)
    if (processing && !pollRef.current) {
      pollRef.current = setInterval(() => fetchRef.current(page, pageSize, keyword), 2000)
    } else if (!processing && pollRef.current) {
      clearInterval(pollRef.current); pollRef.current = undefined
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = undefined } }
  }, [docs, page, pageSize, keyword])

  const handleSearch = () => {
    setPage(1)
    setKeyword(inputKeyword.trim())
  }

  const handleArchive = (d: KBDocument) => async () => {
    const res = await archiveDocument(d.id)
    message.success(res?.stale_qa_count ? `已作废，${res.stale_qa_count} 条历史答案已标记过期` : '已作废')
    fetchDocs(page, pageSize, keyword)
  }

  const handleDelete = (d: KBDocument) => async () => {
    const res = await deleteDocument(d.id)
    message.success(res?.stale_qa_count ? `已删除，${res.stale_qa_count} 条历史答案已标记过期` : '已删除')
    fetchDocs(page, pageSize, keyword)
  }

  const handleReindex = (d: KBDocument) => async () => {
    await reindexDocument(d.id)
    message.success('已加入入库队列')
    fetchDocs(page, pageSize, keyword)
  }

  const columns: ColumnsType<KBDocument> = [
    {
      title: '文档名称', dataIndex: 'doc_name', ellipsis: true, // 主列不设 width，自动伸缩（规范 5.9.2）
      render: (v: string, r: KBDocument) => (
        <Space size={6}>
          <FileTextOutlined style={{ color: 'var(--au-primary)' }} />
          <Text style={{ color: 'var(--au-slate)' }}>{v}</Text>
          <Tag style={{ margin: 0 }}>{r.doc_version}</Tag>
          <Tag color="geekblue" style={{ margin: 0 }}>{DOC_TYPE_LABEL[r.doc_type] || r.doc_type}</Tag>
          {!r.is_current && r.superseded_by_doc_id > 0 && (
            <Tooltip title={`已被文档 #${r.superseded_by_doc_id} 替代`}><Tag color="default" style={{ margin: 0 }}>历史版本</Tag></Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: <div style={{ textAlign: 'center' }}>状态</div>, dataIndex: 'status', width: 120,
      render: (_: unknown, r: KBDocument) => <StatusTag record={r} />,
    },
    { title: <div style={{ textAlign: 'center' }}>分块</div>, dataIndex: 'chunk_count', width: 100, render: (v: number) => v || '-' },
    { title: <div style={{ textAlign: 'center' }}>类型</div>, dataIndex: 'file_type', width: 120, render: (v: string) => v.toUpperCase() },
    {
      title: <div style={{ textAlign: 'center' }}>大小</div>, dataIndex: 'file_size', width: 120,
      render: (v: number) => `${(v / 1024).toFixed(1)} KB`,
    },
    {
      title: '上传时间', dataIndex: 'create_time', width: 200,
      render: (v: string) => (v ? <span style={{ whiteSpace: 'nowrap' }}>{new Date(v).toLocaleString('zh-CN')}</span> : '-'),
    },
    {
      title: '操作', width: 300, fixed: 'right',
      render: (_: unknown, r: KBDocument) => (
        <Space size={0} wrap={false}>
          <Button type="link" size="small" onClick={() => setChunkDoc(r)}>分块</Button>
          <Button type="link" size="small" onClick={() => setContentDoc(r)}>原文</Button>
          {(r.status === 6 || r.status === 7) && (
            <Popconfirm
              title="重新解析并替换旧分块，确定？"
              okText="确定" cancelText="取消"
              onConfirm={() => handleReindex(r)()}>
              <Button type="link" size="small">{r.status === 7 ? '重试' : '重建'}</Button>
            </Popconfirm>
          )}
          {r.status !== 8 && (
            <Popconfirm title="作废后不再参与检索，确定？" okText="确定" cancelText="取消"
              okButtonProps={{ danger: true }}
              onConfirm={() => handleArchive(r)()}>
              <Button type="link" size="small" danger>作废</Button>
            </Popconfirm>
          )}
          <Popconfirm title="确定删除此文档？" okText="确定" cancelText="取消"
            okButtonProps={{ danger: true }}
            onConfirm={() => handleDelete(r)()}>
            <Button type="link" size="small" danger>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <div className="aurora-page aurora-page--fit">
      {/* ① 页头 */}
      <div className="page-header">
        <Title level={4} style={{ margin: 0, fontWeight: 600 }}>知识库</Title>
        <Space>
          {tab === 'docs' && (
            <>
              <Button onClick={() => fetchDocs(page, pageSize, keyword)}>刷新</Button>
              <Button type="primary" disabled={!currentProject}
                onClick={() => setUploadOpen(true)}>上传文档</Button>
            </>
          )}
        </Space>
      </div>

      {/* 页签（文档管理 / 智能问答 / 规则管理 / 评估看板） */}
      <Tabs activeKey={tab} onChange={setTab} style={{ flexShrink: 0 }}
        items={[
          { key: 'docs', label: '文档管理' },
          { key: 'chat', label: '智能问答' },
          { key: 'rules', label: '规则管理' },
          { key: 'eval', label: '评估看板' },
        ]} />

      {tab === 'docs' ? (
        <>
          {/* ② 搜索栏：关键词搜索 + 条数统计 */}
          <div className="search-bar" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Space.Compact>
              <Input placeholder="搜索文档名称" allowClear style={{ minWidth: 200 }}
                value={inputKeyword}
                onChange={(e) => setInputKeyword(e.target.value)}
                onPressEnter={handleSearch} />
              <Button type="primary" onClick={handleSearch}
                style={{ background: 'var(--au-primary)', borderColor: 'var(--au-primary)', color: '#fff' }}>
                查询
              </Button>
            </Space.Compact>
            <Text type="secondary" style={{ marginLeft: 'auto', fontSize: 13 }}>
              共 {total} 份文档，当前生效 {docs.filter((d) => d.is_current && d.status === 6).length} 份
            </Text>
          </div>

          {/* ③ 内容卡片：Table 直接作为子元素 */}
          <div className="content-card">
            {!currentProject ? (
              <div style={{ padding: 40, textAlign: 'center', color: 'var(--au-text-faint)' }}>请先在顶部选择项目</div>
            ) : (
              <Table rowKey="id" loading={loading} dataSource={docs} columns={columns} size="middle"
                scroll={{ y: '100%' }} tableLayout="fixed"
                locale={{ emptyText: '暂无文档，点击右上角「上传文档」导入需求/设计文档' }}
                pagination={{
                  current: page, pageSize, total, showSizeChanger: true,
                  showTotal: (t) => `共 ${t} 条`,
                  onChange: (p, ps) => { setPage(p); setPageSize(ps) },
                }} />
            )}
          </div>
        </>
      ) : tab === 'chat' ? (
        <div className="content-card" style={{ overflow: 'auto' }}>
          <ChatPanel projectId={currentProject?.id ?? 0} />
        </div>
      ) : tab === 'rules' ? (
        <RulesPanel projectId={currentProject?.id ?? 0} />
      ) : (
        <EvalPanel projectId={currentProject?.id ?? 0} />
      )}

      <UploadModal open={uploadOpen} projectId={currentProject?.id ?? 0}
        onClose={() => setUploadOpen(false)} onUploaded={() => fetchDocs(page, pageSize, keyword)} />
      <ChunkDrawer doc={chunkDoc} onClose={() => setChunkDoc(null)} />
      <ContentDrawer doc={contentDoc} onClose={() => setContentDoc(null)} />
    </div>
  )
}
